// 结算弹窗「自动带入最佳优惠券」的纯函数工具：
// 排序与比价逻辑抽出以便单元测试，探测编排（串行试算）在 EcardCheckoutPopup 内。

// 单次探测请求超时（毫秒）：previewProbe 挂起（网络停滞/后端无响应）时不永久锁死
// 支付按钮（probePending 无 finally 兜底），超时按失败处理、由探测链 catch 接住
export const PROBE_TIMEOUT_MS = 8000

/**
 * 给 promise 加超时：超时 reject（原 promise 结果被丢弃），先完成则清除定时器。
 * fetch 层无 AbortController 可传，请求本身无法中止，只保证调用方不被永久挂起。
 *
 * @param {Promise} promise 被包装的请求
 * @param {number} [ms] 超时毫秒，默认 PROBE_TIMEOUT_MS
 * @returns {Promise}
 */
export const runWithTimeout = (promise, ms = PROBE_TIMEOUT_MS) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('probe timeout')), ms)
    promise.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (error) => {
        clearTimeout(timer)
        reject(error)
      }
    )
  })

// 折扣率 → 排序权重：0 元券（rate=0，club 兑换券）最优先；缺失/非法 rate 视为非折扣券排最后。
// 注意 null/空串要显式视为缺失：Number(null)/Number('') 是 0，不拦截会被误当 0 元券排最前
const rateWeight = (coupon) => {
  const raw = coupon && coupon.discountRate
  const rate = raw == null || raw === '' ? NaN : Number(raw)
  return Number.isNaN(rate) ? 2 : rate
}

/**
 * 优惠券按折扣率升序排序（不改原数组）：rate 越小优惠越大（优惠 = 实体卡小计 × (1−rate)），
 * 排序即探测顺序——串行试算第一张通过的券即为最优可用券。0 元券 rate=0 天然排最前。
 *
 * @param {Array<{id: *, discountRate: number|string}>} coupons 可用券列表（coupons 接口 content）
 * @returns {Array} 排序后的新数组
 */
export const sortCouponsByRate = (coupons) => [...(coupons || [])].sort((a, b) => rateWeight(a) - rateWeight(b))

/**
 * 试算结果是否比无券基线更便宜（金额均为后端返回的字符串，Number 化比较）。
 * 基线缺失（无券试算失败）时不比较，仅判定结果有效——防御 rate 异常数据选出更贵的券。
 *
 * @param {{totalPrice?: string|null}|null} preview 带券试算响应
 * @param {string|null|undefined} baseTotalPrice 无券试算的 totalPrice
 * @returns {boolean}
 */
export const isPreviewCheaper = (preview, baseTotalPrice) =>
  !!preview &&
  preview.totalPrice != null &&
  (baseTotalPrice == null || Number(preview.totalPrice) < Number(baseTotalPrice))
